import { goBack, push } from 'connected-react-router';
import { combineReducers } from 'redux';
import { call, put, putResolve, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { DocumentConsignmentResponse, DocumentTemplateInfo, StaticDocumentInfo } from 'generated/bffDocument/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  loadingActions,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { ROUTES } from 'helpers/routes/routes';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { InternalUser } from 'models/context/InternalUser';
import { CustomerMasterSourceType } from 'models/swagger/customerMasterSourceType';

import { PutConsentsRequest } from 'models/swagger/MarketingConsent/PutConsentsRequest';

import { api as consentsApi } from 'saga/marketingConsents/api';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { selectContextCustomerInfo, setContextCustomerActions } from 'saga/context/customer/ducks';
import { selectContextInternalUser } from 'saga/context/internal/ducks';

/* STATE */
export interface DocumentConsignmentState {
  loadingStatus: LoadingStatus;
  documentConsignment: DocumentConsignmentResponse | null;
  documentTemplateList: Array<DocumentTemplateInfo>;
  staticDocumentList: Array<StaticDocumentInfo> | null;
  processingStep: 'EDIT' | 'APPROVE' | 'SIGNING' | null;
  documentStatus: 'OK' | 'ERROR' | 'NOTSIGNED' | null;
}

/* ACTION TYPES */
export enum DocumentConsignmentActionTypes {
  PostDocumentConsignment = '@@Document/POST_DOCUMENTS_CONSIGNMENT',
  GetDocumentTemplateList = '@@Document/GET_TEMPLATE_LIST',
  GetStaticDocumentList = '@@Document/GET_STATIC_DOCUMENT_LIST',
  CheckDocumentStatus = '@@Document/CHECK_DOCUMENT_STATUS',
  SetProcessingStep = '@@Document/SET_PROCESSING_STEP',
  SetDocumentConsignment = '@@Document/SET_DOCUMENT_CONSIGNMENT',
}

/* ACTIONS */
export const postDocumentConsignmentActions = createApiActionCreators(
  DocumentConsignmentActionTypes.PostDocumentConsignment
);
export const getDocumentTemplateListActions = createApiActionCreators(
  DocumentConsignmentActionTypes.GetDocumentTemplateList
);
export const getStaticDocumentListActions = createApiActionCreators(
  DocumentConsignmentActionTypes.GetStaticDocumentList
);
export const checkDocumentStatusActions = createApiActionCreators(DocumentConsignmentActionTypes.CheckDocumentStatus);
export const setProcessingStepAction = createActionCreator(DocumentConsignmentActionTypes.SetProcessingStep);
export const setDocumentConsignmentActions = createActionCreator(DocumentConsignmentActionTypes.SetDocumentConsignment);

/* REDUCERS */
const initialState: DocumentConsignmentState = {
  loadingStatus: LoadingStatus.idle,
  documentConsignment: null,
  documentTemplateList: [],
  staticDocumentList: null,
  processingStep: null,
  documentStatus: null,
};

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  loadingActions([
    DocumentConsignmentActionTypes.CheckDocumentStatus,
    DocumentConsignmentActionTypes.PostDocumentConsignment,
    DocumentConsignmentActionTypes.SetDocumentConsignment,
    DocumentConsignmentActionTypes.CheckDocumentStatus,
  ])
);

const documentConsignment = createReducer(initialState.documentConsignment, {
  [DocumentConsignmentActionTypes.PostDocumentConsignment]: {
    [RequestActionTypes.SUCCESS]: (state: DocumentConsignmentResponse, payload: DocumentConsignmentResponse) => {
      return payload;
    },
    [RequestActionTypes.FAILURE]: () => [],
  },
  [DocumentConsignmentActionTypes.SetDocumentConsignment]: (
    state: DocumentConsignmentResponse,
    payload: DocumentConsignmentResponse
  ) => payload,
});

const documentTemplateList = createReducer(initialState.documentTemplateList, {
  [DocumentConsignmentActionTypes.GetDocumentTemplateList]: {
    [RequestActionTypes.SUCCESS]: (state: Array<DocumentTemplateInfo>, payload: Array<DocumentTemplateInfo>) => payload,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const staticDocumentList = createReducer(initialState.staticDocumentList, {
  [DocumentConsignmentActionTypes.GetStaticDocumentList]: {
    [RequestActionTypes.SUCCESS]: (state: Array<StaticDocumentInfo>, payload: Array<StaticDocumentInfo>) => payload,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const processingStep = createReducer(initialState.processingStep, {
  [DocumentConsignmentActionTypes.SetProcessingStep]: (state: string | null, payload: string | null) => payload,
});

const documentStatus = createReducer(initialState.documentStatus, {
  [DocumentConsignmentActionTypes.CheckDocumentStatus]: {
    [RequestActionTypes.SUCCESS]: (state: string | null, payload: string | null) => payload,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

export default combineReducers<DocumentConsignmentState>({
  loadingStatus,
  documentConsignment,
  documentTemplateList,
  staticDocumentList,
  processingStep,
  documentStatus,
});

/* SELECTORS*/
const selectConsignmentState = (state: AppState): DocumentConsignmentState => state.documentConsignment;

export const selectDocumentConsignment = (state: AppState): DocumentConsignmentResponse =>
  selectConsignmentState(state).documentConsignment || {};

export const selectDocumentTemplateList = (state: AppState): Array<DocumentTemplateInfo> =>
  selectConsignmentState(state).documentTemplateList;

export const selectStaticDocumentList = (state: AppState): Array<StaticDocumentInfo> | null =>
  selectConsignmentState(state).staticDocumentList;

export const selectProcessingStep = (state: AppState): string | null => selectConsignmentState(state).processingStep;

export const selectDocumentStatus = (state: AppState): string | null => selectConsignmentState(state).documentStatus;

export const selectDocumentsLoadingStatus = (state: AppState): LoadingStatus =>
  selectConsignmentState(state).loadingStatus;

/* SAGAS */
function* postDocumentConsignment({ payload }: any) {
  const request: ApiClientRequest = {
    apiCall: call(api.postDocumentConsignment, yield getApiUrl(environment.urlConfig.documents), payload),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    if (resp.data) {
      yield put(postDocumentConsignmentActions.success(resp.data));

      if (!resp.data.url && !resp.data.isBiometric) {
        yield put(goBack());
      } else {
        const consignment: DocumentConsignmentResponse = yield select(selectDocumentConsignment);
        const journalJobPk = consignment.journalJobPk ? consignment.journalJobPk : '';

        yield put(
          push(
            `${buildRoute(ROUTES.DocumentSendingDeeplink, { journalJobPk: journalJobPk })}?journalJobPkHash=${
              consignment.journalJobPkHash
            }&isBiometric=${consignment.isBiometric}${consignment.url ? `&url=${consignment.url}` : ''}`
          )
        );
      }
    } else {
      yield put(postDocumentConsignmentActions.failure());
    }
  } else {
    yield put(getKahlActions.request({ infoMessageIds: 'ID0908' }));
    yield put(postDocumentConsignmentActions.failure());
  }
}

function* getDocumentTemplateList() {
  const request: ApiClientRequest = {
    apiCall: call(api.getDocumentTemplateList, yield getApiUrl(environment.urlConfig.documents)),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    if (resp.data) {
      yield put(getDocumentTemplateListActions.success(resp.data));
    } else {
      yield put(getDocumentTemplateListActions.failure());
    }
  } else {
    yield put(getDocumentTemplateListActions.failure());
  }
}

function* getStaticDocumentList() {
  const request: ApiClientRequest = {
    apiCall: call(api.getStaticDocumentList, yield getApiUrl(environment.urlConfig.documents)),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    if (resp.data) {
      yield put(getStaticDocumentListActions.success(resp.data));
    } else {
      yield put(getStaticDocumentListActions.failure());
    }
  } else {
    yield put(getStaticDocumentListActions.failure());
  }
}

function* checkDocumentStatus({ payload }: any) {
  const consignment: DocumentConsignmentResponse = yield select(selectDocumentConsignment);
  const processingStep: string = yield select(selectProcessingStep);

  const request: ApiClientRequest = {
    apiCall: call(
      api.checkDocumentStatus,
      yield getApiUrl(environment.urlConfig.documents),
      processingStep,
      consignment.journalJobPk || ''
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(checkDocumentStatusActions.success(resp.data));

    const consignment: DocumentConsignmentResponse = yield select(selectDocumentConsignment);
    const status = resp.data.status;

    if (payload.actionType === 'init') {
      if (status === 'OK') {
        yield put(getKahlActions.request({ infoMessageIds: 'ID1711' }));
      }

      return;
    }

    if (!payload.isSigning) {
      if (!consignment.isBiometric && ['EDIT', 'APPROVAL'].includes(processingStep)) {
        if (status === 'OK') {
          yield put(push(ROUTES.DocumentList));
        } else if (processingStep === 'EDIT') {
          yield put(getKahlActions.request({ infoMessageIds: 'ID1610' }));
        } else {
          yield put(getKahlActions.request({ infoMessageIds: 'ID1611' }));
        }
      }

      if (consignment.isBiometric && processingStep === 'SIGNING') {
        if (status === 'OK') {
          if (payload.actionType === 'marketingAgreement' || payload.actionType === 'marketingAdvertisementAgreement') {
            const customer: CustomerInfo = yield select(selectContextCustomerInfo);
            const internalUser: InternalUser = yield select(selectContextInternalUser);

            const consentPartyType =
              payload.actionType === 'marketingAgreement'
                ? CustomerMasterSourceType.O2POP
                : CustomerMasterSourceType.O2POPD;
            const requestBody: PutConsentsRequest = {
              cuRefNo: customer.cuRefNo,
              userIdentification: internalUser?.login,
              date: new Date(),
              messageVersion: payload.messageVersion,
              partyType: consentPartyType,
            };

            const request: ApiClientRequest = {
              apiCall: call(consentsApi.putConsents, yield getApiUrl(environment.urlConfig.userMgw), requestBody),
            };
            const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

            if (resp.ok) {
              if (consentPartyType === CustomerMasterSourceType.O2POPD) {
                yield putResolve(
                  setContextCustomerActions.success({
                    ...customer,
                    marketingAgreement: true,
                  })
                );
              }

              yield put(push(ROUTES.DocumentList));
            } else if (consentPartyType === CustomerMasterSourceType.O2POPD) {
              yield putResolve(
                setContextCustomerActions.success({
                  ...customer,
                  marketingAgreement: false,
                })
              );
            }
          } else {
            yield put(push(ROUTES.DocumentList));
          }
        }

        if (status === 'NOTSIGNED') {
          yield put(getKahlActions.request({ infoMessageIds: 'ID1079' }));
          yield put(push(ROUTES.DocumentList));
        }

        if (status === 'ERROR') {
          yield put(getKahlActions.request({ infoMessageIds: 'ID1611' }));
        }
      }
    } else if (status === 'OK') {
      yield put(setProcessingStepAction('SIGNING'));
    } else {
      yield put(getKahlActions.request({ infoMessageIds: 'ID1610' }));
    }
  } else {
    yield put(getKahlActions.request());
    yield put(checkDocumentStatusActions.failure());
  }
}

/* EXPORT */
export function* documentConsignmentSaga() {
  yield takeLatest(
    createActionType(DocumentConsignmentActionTypes.PostDocumentConsignment, RequestActionTypes.REQUEST),
    postDocumentConsignment
  );

  yield takeLatest(
    createActionType(DocumentConsignmentActionTypes.GetDocumentTemplateList, RequestActionTypes.REQUEST),
    getDocumentTemplateList
  );

  yield takeLatest(
    createActionType(DocumentConsignmentActionTypes.GetStaticDocumentList, RequestActionTypes.REQUEST),
    getStaticDocumentList
  );

  yield takeLatest(
    createActionType(DocumentConsignmentActionTypes.CheckDocumentStatus, RequestActionTypes.REQUEST),
    checkDocumentStatus
  );
}
